<script setup lang="ts">
import { AppModal, AppButton } from '@libs/ui';
import type { Pax } from '@libs/trip-planner-interfaces';

import type { RoomPaxConfig } from './PaxSelector.model';
import { usePaxSelectorStore } from './stores/paxSelectorStore';
import PaxSelectorView from './PaxSelectorView.vue';

interface PaxSelectorModalProps {
  initialPaxes: RoomPaxConfig[];
  shouldShowTicketsWarning: boolean;
  shouldShowRoomSelectionWarning: boolean;
  shouldShowNumberOfVehiclesUpdatedWarning: boolean;
  shouldShowNumberOfVehiclesWillBeUpdatedWarning: boolean;
}

const props = defineProps<PaxSelectorModalProps>();

interface PaxSelectorModalEmits {
  (eventName: 'confirm-modal', newPax: Pax): void;
  (eventName: 'cancel-modal'): void;
}

const emit = defineEmits<PaxSelectorModalEmits>();

const paxSelectorStore = usePaxSelectorStore();
</script>

<template>
  <AppModal
    max-height="654px"
    max-width="600px"
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('cancel-modal')"
    @confirm="emit('confirm-modal', paxSelectorStore.asPax)"
  >
    <section class="pax-selector-modal">
      <main class="top-section">
        <PaxSelectorView
          :initial-paxes="props.initialPaxes"
          :should-show-tickets-warning="props.shouldShowTicketsWarning"
          :should-show-room-selection-warning="
            props.shouldShowRoomSelectionWarning
          "
          :emit-debounce-time-ms="0"
          :should-show-number-of-vehicles-updated-warning="
            props.shouldShowNumberOfVehiclesUpdatedWarning
          "
          :should-show-number-of-vehicles-will-be-updated-warning="
            props.shouldShowNumberOfVehiclesWillBeUpdatedWarning
          "
        />
      </main>
      <footer class="footer">
        <div class="buttons-container">
          <AppButton
            state="enabled"
            type="secondary"
            :wide="true"
            size="large"
            @click="emit('cancel-modal')"
          >
            {{ $t('planner.close') }}
          </AppButton>
          <AppButton
            state="enabled"
            type="primary"
            :wide="true"
            size="large"
            @click="emit('confirm-modal', paxSelectorStore.asPax)"
          >
            {{ $t('planner.done') }}
          </AppButton>
        </div>
      </footer>
    </section>
  </AppModal>
</template>

<style lang="scss" scoped>
.pax-selector-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  .top-section {
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-32) var(--spacing-24) var(--spacing-24)
      var(--spacing-24);
    min-height: 0;
  }
  .footer {
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    padding: var(--spacing-24);
    border-top: 1px solid var(--color-neutral-30);
    justify-content: flex-end;
    .buttons-container {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-16);
      width: 266px;
    }
  }
}
</style>
